import api from './api';
import type {
  Customer,
  CustomerCreateLoginResponse,
  CustomerCreateRequest,
  CustomerFilters,
  PaginatedResponse,
  Policy,
} from '@/types';

export const customerService = {
  async getCustomers(
    page: number = 1,
    filters?: CustomerFilters
  ): Promise<PaginatedResponse<Customer>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.search) params.append('search', filters.search);
    if (filters?.customer_type) params.append('customer_type', filters.customer_type);
    if (filters?.contact_type) params.append('contact_type', filters.contact_type);
    if (filters?.segment) params.append('segment', filters.segment);
    if (filters?.kyc_status) params.append('kyc_status', filters.kyc_status);
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters?.page_size) params.append('page_size', String(filters.page_size));

    const response = await api.get<PaginatedResponse<Customer>>(`/customers/?${params.toString()}`);
    return response.data;
  },

  async getCustomer(id: string): Promise<Customer> {
    const response = await api.get<Customer>(`/customers/${id}/`);
    return response.data;
  },

  async getCustomerPolicies(id: string): Promise<PaginatedResponse<Policy>> {
    const response = await api.get<PaginatedResponse<Policy>>(`/customers/${id}/policies/`);
    return response.data;
  },

  async createCustomer(data: CustomerCreateRequest): Promise<Customer> {
    const response = await api.post<Customer>('/customers/', data);
    return response.data;
  },

  async updateCustomer(id: string, data: Partial<CustomerCreateRequest>): Promise<Customer> {
    const response = await api.patch<Customer>(`/customers/${id}/`, data);
    return response.data;
  },

  async deleteCustomer(id: string): Promise<void> {
    await api.delete(`/customers/${id}/`);
  },

  async updateKyc(id: string, data: { kyc_status: string; kyc_details?: Record<string, unknown> }): Promise<Customer> {
    const response = await api.post<Customer>(`/customers/${id}/update_kyc/`, data);
    return response.data;
  },

  async toggleActive(id: string): Promise<Customer> {
    const response = await api.post<{ message: string; customer: Customer }>(
      `/customers/${id}/toggle-active/`
    );
    return response.data.customer;
  },

  async getMe(): Promise<Customer> {
    const response = await api.get<Customer>('/customers/me/');
    return response.data;
  },

  async updateMe(data: Partial<CustomerCreateRequest>): Promise<Customer> {
    const response = await api.patch<Customer>('/customers/me/', data);
    return response.data;
  },

  /** Item 8: broker-admin provisions (or links, if the phone matches an existing login) a customer's login. */
  async createLogin(id: string): Promise<CustomerCreateLoginResponse> {
    const response = await api.post<CustomerCreateLoginResponse>(`/customers/${id}/create-login/`);
    return response.data;
  },

  /** Candidate duplicate groups for the caller's own broker. Read-only. */
  async duplicates(): Promise<DuplicatesResponse> {
    const response = await api.get<DuplicatesResponse>('/customers/duplicates/');
    return response.data;
  },

  /** Fold `losingIds` into `survivorId`. Destructive and explicit — the caller
   *  must have confirmed it. */
  async merge(survivorId: string, losingIds: string[]): Promise<MergeResponse> {
    const response = await api.post<MergeResponse>(`/customers/${survivorId}/merge/`, {
      losing_ids: losingIds,
    });
    return response.data;
  },
};

/** What is attached to a candidate, so a reviewer can see what is at stake. */
export interface DuplicateCounts {
  policies: number;
  quotes: number;
  claims: number;
  documents: number;
  leads: number;
}

export interface DuplicateMember {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  created_at: string | null;
  counts: DuplicateCounts;
}

/**
 * `exact` — name AND email AND phone all match.
 * `similar_name` — same phone plus a near-identical name. Weaker evidence:
 * brokers often put their own number on a customer record, so the server also
 * refuses to group a number shared by more than a handful of contacts.
 */
export type DuplicateMatch = 'exact' | 'similar_name';

export interface DuplicateGroup {
  key: string;
  match: DuplicateMatch;
  customers: DuplicateMember[];
}

export interface DuplicatesResponse {
  groups: DuplicateGroup[];
}

export interface MergeResponse {
  survivor: string;
  moved: Record<string, number>;
  dropped: Record<string, number>;
  merged_ids: string[];
}
